import { Fragment, useId, useState } from "react";
import { GrundartikelLink } from "@/components/catalog/GrundartikelLink";
import {
  CARRIER_TIERS,
  type CarrierTier,
  type KundenversandOption,
  type SetCalcResult,
  type SetPreviewRow,
  type KombiPreviewRow,
} from "@/api/pricing";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { TIER_LABEL, UNGUELTIG } from "./csv";

/** Endliche Zahl? `NaN`/`Infinity` kommen per JSON als `null` an. */
function isNum(n: number | null | undefined): n is number {
  return typeof n === "number" && Number.isFinite(n);
}

/** `15.21` -> `"15,21"` (de-DE, immer 2 Nachkommastellen, mit Tausenderpunkt); fehlend -> „—". */
export function fmtNum(n: number | null | undefined): string {
  return isNum(n) ? n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—";
}

function fmtGram(n: number | null | undefined): string {
  return isNum(n) ? `${n.toLocaleString("de-DE", { maximumFractionDigits: 0 })} g` : "—";
}

function fmtPct(fraction: number | null | undefined): string {
  return isNum(fraction) ? `${(fraction * 100).toLocaleString("de-DE", { maximumFractionDigits: 2 })} %` : "—";
}

const BAD = "border-transparent bg-coral-soft text-coral";
const SOFT = "border-transparent bg-warn-soft text-warn";
const NEUTRAL = "border-transparent bg-panel-2 text-ink-2";

/** Reihenfolge = Stapel von unten (eigene Kosten) nach oben (Abgaben). */
const STACK_PARTS = [
  { key: "ek_plus_versand", label: "EK + Versand", swatch: "bg-ink" },
  { key: "marge", label: "Marge", swatch: "bg-success" },
  { key: "puffer", label: "Puffer", swatch: "bg-warn" },
  { key: "werbung", label: "Werbung", swatch: "bg-coral" },
  { key: "amazon_gebuehr", label: "Amazon-Gebühr", swatch: "bg-primary" },
  { key: "ust", label: "USt", swatch: "bg-ink-2" },
] as const;

interface Props {
  rows: SetPreviewRow[];
  kombiRows?: KombiPreviewRow[];
  kombiNames?: Record<string, Record<string, string>>;
  optionen: KundenversandOption[];
  /** Key der im Kopf gewählten Kundenversand-Option (Spalte „Plus-Versand"). */
  kundenversandKey: string;
  tiers: Record<string, CarrierTier>;
  onTierChange: (sku: string, tier: CarrierTier) => void;
}

const COLS = 8;

export function SetPreviewTable({ rows, kombiRows = [], kombiNames = {}, optionen, kundenversandKey, tiers, onTierChange }: Props) {
  const selected = optionen.find((o) => o.key === kundenversandKey);
  return (
    <div className="rounded-md border border-line bg-panel overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-line text-[11px] uppercase tracking-wide text-ink-2">
            <th className="text-right font-medium pl-4 pr-2 py-2 w-16">Stück</th>
            <th className="text-right font-medium px-2 py-2">EK · n</th>
            <th className="text-right font-medium px-2 py-2">Gewicht · n</th>
            <th className="text-left font-medium px-2 py-2">Stufe</th>
            <th className="text-right font-medium px-2 py-2">Versand</th>
            <th className="text-right font-medium px-2 py-2">Break-Even (Gratisversand)</th>
            <th className="text-right font-medium px-2 py-2 text-ink">VK (Gratisversand)</th>
            <th className="text-right font-medium pl-2 pr-4 py-2">
              Plus-Versand
              {selected && <span className="block normal-case tracking-normal">{selected.label} · {fmtNum(selected.betrag)}</span>}
            </th>
          </tr>
        </thead>
        <tbody>
          {[...rows, ...kombiRows].map((row) => {
            const id = "key" in row ? `kombi:${row.key}` : row.sku;
            return <Fragment key={id}>
              <ArticleHead row={row} id={id} names={"key" in row ? kombiNames[row.key] : undefined} tier={tiers[id] ?? "auto"} onTierChange={onTierChange} />
              {row.sets.map((set) => (
                <SetRow
                  key={set.quantity}
                  sku={id}
                  set={set}
                  optionen={optionen}
                  kundenversandKey={kundenversandKey}
                />
              ))}
            </Fragment>;
          })}
        </tbody>
      </table>
    </div>
  );
}

function ArticleHead({
  row,
  id,
  names,
  tier,
  onTierChange,
}: {
  row: SetPreviewRow | KombiPreviewRow;
  id: string;
  names?: Record<string, string>;
  tier: CarrierTier;
  onTierChange: (sku: string, tier: CarrierTier) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const detailsId = useId();
  const kombi = "key" in row;
  // Vorbelegung der Kaskade je Stückzahl; unterschiedliche Stufen als „brief/dhl".
  const autoTiers = [...new Set(row.sets.map((s) => TIER_LABEL[s.carrier_tier_auto]))].join("/");
  return (
    <>
    <tr className="border-t-2 border-line bg-panel-2/60">
      <td colSpan={COLS} className="px-4 py-2.5">
        <div className="flex items-center gap-x-3 gap-y-1.5 flex-wrap">
          {/* Sternnummer ist die Arbeits-Kennung; die DR-SKU steht gedämpft dahinter. */}
          {kombi ? <span className="font-mono text-xs font-semibold text-primary break-words">{row.label}</span> : row.merchant_sku ? (
            <span className="flex items-baseline gap-1.5">
              <GrundartikelLink code={row.merchant_sku} className="font-mono text-xs font-semibold text-primary" />
              <span className="font-mono text-[11px] text-ink-2">{row.sku}</span>
            </span>
          ) : (
            <span className="font-mono text-xs font-semibold text-primary">{row.sku}</span>
          )}
          {!kombi && <span className="text-ink font-medium truncate max-w-[360px]" title={row.name}>{row.name}</span>}
          <Badge variant="outline" className={NEUTRAL}>{kombi ? "Kombi" : row.is_grundartikel ? "Grundartikel" : "Variante"}</Badge>
          {kombi && <button type="button" aria-label={`${row.components.length} Komponenten ${id}`} aria-expanded={expanded} aria-controls={detailsId} onClick={() => setExpanded(prev => !prev)} className="rounded-sm text-xs text-primary underline decoration-dotted underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">{row.components.length} Komponenten</button>}
          <span className="text-xs text-ink-2">
            Marge <span className="font-mono text-ink">{fmtPct(row.margin_pct)}</span>
            {" "}({row.margin_source === "config" ? "eingestellt" : "Standard"})
          </span>
          <span className="text-xs text-ink-2">
            Gewicht <span className="font-mono text-ink">{fmtGram(row.weight_g)}</span>
          </span>
          <span className="text-xs text-ink-2">
            EK <span className="font-mono text-ink">{fmtNum(row.ek_unit)}</span>
          </span>
          {row.ek_missing && <Badge variant="outline" className={BAD}>EK fehlt</Badge>}
          {row.ek_zero && <Badge variant="outline" className={SOFT}>EK 0</Badge>}
          {row.weight_missing && <Badge variant="outline" className={BAD}>Gewicht fehlt</Badge>}
          {row.weight_suspect && <Badge variant="outline" className={BAD}>Gewicht Platzhalter?</Badge>}
          {!kombi && row.dims_missing && <Badge variant="outline" className={BAD}>Maße fehlen</Badge>}
          <label className="ml-auto flex items-center gap-2 text-xs text-ink-2">
            Versandstufe
            <select
              aria-label={`Versandstufe ${id}`}
              value={tier}
              onChange={(e) => {
                const next = CARRIER_TIERS.find((t) => t === e.target.value);
                if (next) onTierChange(id, next);
              }}
              className={cn(
                "h-7 rounded-md border bg-panel px-1.5 text-xs text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                tier === "auto" ? "border-line" : "border-primary",
              )}
            >
              {CARRIER_TIERS.map((t) => (
                <option key={t} value={t}>
                  {t === "auto" ? `auto (→ ${autoTiers})` : TIER_LABEL[t]}
                </option>
              ))}
            </select>
          </label>
        </div>
      </td>
    </tr>
    {kombi && expanded && <tr className="border-t border-line-2 bg-primary-soft/40">
      <td colSpan={COLS} className="px-4 py-3">
        <div id={detailsId} role="region" aria-label={`Komponenten ${id}`}>
          <p className="text-[11px] text-ink-2 mb-2">Komponenten je Kombi · EK und Gewicht je Artikel</p>
          <ul className="space-y-2">
            {row.components.map(c => <li key={c.sku} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
              <GrundartikelLink code={c.merchant_sku ?? c.sku} className="font-mono font-semibold text-primary">{c.merchant_sku ?? c.sku}</GrundartikelLink>
              {c.merchant_sku && <span className="font-mono text-ink-2">{c.sku}</span>}
              <span className="text-ink">{names?.[c.sku] ?? ""}</span>
              <span className="font-mono text-ink">×{c.qty}</span>
              <span className="text-ink-2">EK <span className="font-mono text-ink">{fmtNum(c.ek_unit)} €</span></span>
              <span className="text-ink-2">Gewicht <span className="font-mono text-ink">{fmtGram(c.weight_g)}</span></span>
              {c.is_grundartikel && <Badge variant="outline" className={NEUTRAL}>Grundartikel</Badge>}
            </li>)}
          </ul>
        </div>
      </td>
    </tr>}
    </>
  );
}

function SetRow({
  sku,
  set,
  optionen,
  kundenversandKey,
}: {
  sku: string;
  set: SetCalcResult;
  optionen: KundenversandOption[];
  kundenversandKey: string;
}) {
  const invalid = set.invalid;
  const ungueltig = <span className="text-coral">{UNGUELTIG}</span>;
  const price = (n: number) => (invalid ? ungueltig : fmtNum(n));
  const plus = set.plus_versand[kundenversandKey];
  return (
    <tr className="border-t border-line-2 hover:bg-panel-2/50">
      <td className="text-right font-mono pl-4 pr-2 py-1.5 text-ink">{set.quantity}</td>
      <td className="text-right font-mono px-2 py-1.5 text-ink-2">{fmtNum(set.ek_total)}</td>
      <td className="text-right font-mono px-2 py-1.5 text-ink-2">{fmtGram(set.weight_g_total)}</td>
      <td className="px-2 py-1.5 text-ink-2 whitespace-nowrap">
        {TIER_LABEL[set.carrier_tier_used]}
        {set.bumped && <span className="ml-1 text-[11px] text-warn">hochgestuft</span>}
      </td>
      <td className="text-right font-mono px-2 py-1.5 text-ink-2">{price(set.shipping)}</td>
      <td className="text-right font-mono px-2 py-1.5 text-ink-2">{price(set.break_even_inkl)}</td>
      <td className="text-right font-mono px-2 py-1.5">
        {invalid ? (
          price(set.preis_inkl)
        ) : (
          <StackPopover sku={sku} set={set} optionen={optionen} />
        )}
      </td>
      <td className="text-right font-mono pl-2 pr-4 py-1.5 text-ink-2 whitespace-nowrap">
        {/* invalid zuerst: auch eine fehlende Option (z. B. „eigener Betrag“ ohne Eingabe) zeigt „ungültig“. */}
        {invalid ? ungueltig : plus === undefined ? "—" : fmtNum(plus)}
        {!invalid && set.plus_versand_negativ && (
          <Badge
            variant="outline"
            className={cn(SOFT, "ml-1.5 font-sans")}
            title="Kundenversand liegt in mindestens einem Kanal über dem VK — dort auf 0,00 gesetzt (Details im Stapel)"
          >
            Versand &gt; VK
          </Badge>
        )}
      </td>
    </tr>
  );
}

function StackPopover({ sku, set, optionen }: { sku: string; set: SetCalcResult; optionen: KundenversandOption[] }) {
  const total = set.preis_inkl;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Stapel ${sku} ${set.quantity} Stück: VK ${fmtNum(total)}`}
          className="font-semibold text-ink underline decoration-dotted decoration-line underline-offset-4 hover:text-primary hover:decoration-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm"
        >
          {fmtNum(total)}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 border-line bg-panel p-0">
        <div className="px-4 pt-3 pb-2 border-b border-line-2">
          <div className="text-[11px] uppercase tracking-wide text-ink-2">Preis-Stapel · {set.quantity} Stück</div>
          <div className="mt-0.5 flex items-baseline justify-between">
            <span className="font-display font-semibold text-ink">VK (Gratisversand)</span>
            <span className="font-mono font-semibold text-ink">{fmtNum(total)}</span>
          </div>
          {/* Signatur: der Leitpreis als gestapelter Balken, eigene Kosten links, Abgaben rechts. */}
          <div className="mt-2 flex h-2.5 w-full overflow-hidden rounded-sm bg-line-2" aria-hidden="true">
            {STACK_PARTS.map((p) => {
              const amount = set.stack[p.key];
              const width = total > 0 ? (Math.max(0, amount) / total) * 100 : 0;
              return <span key={p.key} className={p.swatch} style={{ width: `${width}%` }} />;
            })}
          </div>
        </div>
        <dl className="px-4 py-2 space-y-1 text-xs">
          {STACK_PARTS.map((p) => (
            <div key={p.key} className="flex items-center gap-2">
              <span className={cn("size-2 rounded-[2px] shrink-0", p.swatch)} />
              <dt className="flex-1 text-ink-2">{p.label}</dt>
              <dd className={cn("font-mono", set.stack[p.key] < 0 ? "text-coral" : "text-ink")}>
                {fmtNum(set.stack[p.key])}
              </dd>
            </div>
          ))}
        </dl>
        {set.stack.puffer_negativ && (
          <p className="mx-4 mb-2 rounded-sm bg-warn-soft px-2 py-1.5 text-[11px] text-warn">
            Puffer negativ: Puffer-Anteil in den Einstellungen prüfen.
          </p>
        )}
        <div className="border-t border-line-2 px-4 py-2">
          <div className="text-[11px] uppercase tracking-wide text-ink-2 mb-1">Plus-Versand je Kanal</div>
          <table className="w-full text-xs">
            <tbody>
              {optionen.map((o) => {
                const value = set.plus_versand[o.key];
                const negativ = o.betrag > total;
                return (
                  <tr key={o.key}>
                    <td className="py-0.5 text-ink-2">{o.label}</td>
                    <td className="py-0.5 pl-2 text-right font-mono text-ink-2" title="Kundenversand">+{fmtNum(o.betrag)}</td>
                    <td className={cn("py-0.5 pl-3 text-right font-mono", negativ ? "text-warn" : "text-ink")}>
                      {value === undefined ? "—" : fmtNum(value)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="mt-1 text-[11px] text-ink-2">Artikelpreis = VK − Kundenversand; unter 0 wird 0,00 gezeigt.</p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
